import React, { useEffect, useState } from "react";

import { UserPermissions } from "@doitintl/cmp-models";
import { Box, Card, Typography, useMediaQuery } from "@mui/material";

import { useAsyncCurrency } from "../../../Components/hooks/useCurrency";
import { useAuthContext } from "../../../Context/AuthContext";
import { useUserContext } from "../../../Context/UserContext";
import { formatDecimalNumber } from "../../../utils/common";
import { savingsPeriodToLabel } from "../constants";
import { useInsightsContext } from "../context";
import { useInsights } from "../hooks";
import { DisplayStatus, type Insight, InsightPriority } from "../types";
import { calculateTotalSavingsForTimePeriod } from "../utils";

type HeaderCardSection = {
  mainContent: string;
  subContent?: string;
  onClick?: () => void;
};

type HeaderCardProps = {
  topSection: HeaderCardSection;
  middleSection: HeaderCardSection;
  bottomSection: HeaderCardSection;
};

const getAllActionableInsights = (insights: Insight[] | undefined) =>
  insights?.filter((insight) => insight.displayStatus === DisplayStatus.ACTIONABLE) ?? [];

const getAllinsightsWithSavings = (insights: Insight[] | undefined) =>
  insights?.filter((insight) => insight.results?.potentialDailySavings?.breakdown) ?? [];

const getInsightsWithHighPriority = (insights: Insight[] | undefined) =>
  insights?.filter((insight) => insight.priority === InsightPriority.High) ?? [];

const getEasyWinsInsights = (insights: Insight[] | undefined) =>
  insights?.filter((insight) => insight.easyWinDescription) ?? [];

const HeaderCard: React.FC<HeaderCardProps> = ({ topSection, middleSection, bottomSection }) => (
  <Card
    variant="outlined"
    sx={{
      p: 3,
      minWidth: 200,
      width: "100%",
    }}
    data-cy="header-summary"
  >
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 500 }}>
        {topSection.mainContent}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "flex-end" }}>
        <Typography variant="h1" sx={{ fontWeight: 500 }}>
          {middleSection.mainContent}
        </Typography>
        {middleSection.subContent && (
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            &nbsp;{middleSection.subContent}
          </Typography>
        )}
      </Box>

      <Typography
        variant="caption"
        color={bottomSection.onClick ? "primary.main" : "textSecondary"}
        onClick={bottomSection.onClick}
        {...(bottomSection.onClick && {
          sx: {
            cursor: "pointer",
          },
          onClick: bottomSection.onClick,
        })}
      >
        {bottomSection.mainContent}
      </Typography>
    </Box>
  </Card>
);

type HeaderSummaryProps = {
  permissionsCoverage: number;
  onCoverageClick: () => void;
};

const HeaderSummary: React.FC<HeaderSummaryProps> = ({ permissionsCoverage, onCoverageClick }) => {
  const isMobile = useMediaQuery("(max-width:700px)");

  const { userRoles } = useUserContext({ requiredRoles: true });

  const { isDoitEmployee } = useAuthContext();

  const { insights } = useInsights();

  const { savingsPeriod } = useInsightsContext();

  const hasInsightsManagerPermission = isDoitEmployee || userRoles.permissions.has(UserPermissions.InsightsManager);

  const totalSavings = calculateTotalSavingsForTimePeriod(insights, savingsPeriod);
  const easyWinSavings = calculateTotalSavingsForTimePeriod(getEasyWinsInsights(insights), savingsPeriod);
  const highPrioritySavings = calculateTotalSavingsForTimePeriod(getInsightsWithHighPriority(insights), savingsPeriod);

  const { asyncConvertCurrency, customerCurrencySymbol } = useAsyncCurrency();

  const [formattedTotalSavings, setFormattedTotalSavings] = useState<string>();
  const [formattedEasyWinsSavings, setFormattedEasyWinsSavings] = useState<string>();
  const [formattedHighPrioritySavings, setFormattedHighPrioritySavings] = useState<string>();

  useEffect(() => {
    asyncConvertCurrency(totalSavings).then((convertedSavings) => {
      setFormattedTotalSavings(`${customerCurrencySymbol}${formatDecimalNumber(convertedSavings, 0)}`);
    });

    asyncConvertCurrency(easyWinSavings).then((convertedSavings) => {
      setFormattedEasyWinsSavings(`${customerCurrencySymbol}${formatDecimalNumber(convertedSavings, 0)}`);
    });

    asyncConvertCurrency(highPrioritySavings).then((convertedSavings) => {
      setFormattedHighPrioritySavings(`${customerCurrencySymbol}${formatDecimalNumber(convertedSavings, 0)}`);
    });
  }, [asyncConvertCurrency, customerCurrencySymbol, totalSavings, easyWinSavings, highPrioritySavings]);

  const cardProps: HeaderCardProps[] = [
    ...(hasInsightsManagerPermission
      ? [
          {
            topSection: { mainContent: "Insights Coverage" },
            middleSection: {
              mainContent: `${permissionsCoverage}%`,
            },
            bottomSection: {
              mainContent: "See what you are missing",
              onClick: onCoverageClick,
            },
          },
        ]
      : []),
    {
      topSection: { mainContent: "Potential Savings" },
      middleSection: {
        mainContent: `${formattedTotalSavings}`,
        subContent: ` / ${savingsPeriodToLabel[savingsPeriod]}`,
      },
      bottomSection: { mainContent: "Total from all insights" },
    },
    {
      topSection: { mainContent: "Actionable" },
      middleSection: { mainContent: `${getAllActionableInsights(insights).length}` },
      bottomSection: {
        mainContent: `${getAllinsightsWithSavings(getAllActionableInsights(insights)).length} with potential savings`,
      },
    },
    {
      topSection: { mainContent: "High priority" },
      middleSection: { mainContent: `${getInsightsWithHighPriority(insights).length}` },
      bottomSection: {
        mainContent: `Savings of ${formattedHighPrioritySavings} / ${savingsPeriodToLabel[savingsPeriod]}`,
      },
    },
    {
      topSection: { mainContent: "Easy wins" },
      middleSection: { mainContent: `${getEasyWinsInsights(insights).length}` },
      bottomSection: { mainContent: `Savings of ${formattedEasyWinsSavings} / ${savingsPeriodToLabel[savingsPeriod]}` },
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 3,
        mt: 4,
      }}
    >
      {cardProps.map((card, i) => (
        <Box
          key={`card-${i}`}
          sx={{
            flexBasis: isMobile ? "100%" : "auto",
            flexGrow: 1,
          }}
        >
          <HeaderCard {...card} />
        </Box>
      ))}
    </Box>
  );
};

export default HeaderSummary;
